import { FormControl, MenuItem, Select, FormLabel } from '@mui/material'
import React, { useEffect } from 'react'
import * as CancerTypes from '../utils/CancerTypes.js'
import { renderContent } from '../utils/helpers.js'
import ConditionalSection from '../widgets/ConditionalSection'
import IntegerDetails from './IntegerDetails.jsx'
import TextFieldQuestion from './TextFieldQuestion.jsx'

export default function SpecificCancerQuestions({
  category,
  cancerType,
  setCancerType,
  setTreatmentLocation,
  treatmentLocation,
  setPatientAddress,
  patientAddress,
  qualifiers,
  setQualifiers,
  handleQualifiersChange,
  secondBreastCancer,
  setSecondBreastCancer,
  getCancerTypes,
  isProband,
  config,
}) {
  useEffect(() => {
    // Should only work for `Breast` or `Breast Cancer`.
    setQualifiers([])
    setSecondBreastCancer()
  }, [])

  const renderCancerTypes = () => {
    const cancerTypes = getCancerTypes(category.label)

    return cancerTypes ? (
      <FormControl key="cancerType" fullWidth>
        <FormLabel>Select Type</FormLabel>
        <Select
          value={cancerType?.id ?? ''}
          onChange={({ target: { value: newCancerTypeId } }) => {
            const _cancerType = cancerTypes.find(({ id }) => id === newCancerTypeId)

            if (newCancerTypeId !== cancerType.id) {
              setCancerType(_cancerType)
            }

            if (cancerType.id && newCancerTypeId !== cancerType.id) {
              setQualifiers([])
              setTreatmentLocation('')
              setPatientAddress('')
            }
          }}
        >
          {cancerTypes.map(({ id, label }) => (
            <MenuItem key={id} value={id}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : (
      renderOtherType()
    )
  }

  const renderOtherType = () => {
    return category.label === 'Other' ? (
      <FormControl key="cancerOtherType">
        <FormLabel>Please Describe</FormLabel>
        <TextFieldQuestion
          fullWidth
          handleChange={(value) => {
            setCancerType({ id: 'other', label: value })
          }}
          value={cancerType?.label ?? ''}
        />
      </FormControl>
    ) : null
  }

  const renderAgeAtDiagnosis = () => {
    return category.label ? (
      <FormControl key="cancerAge">
        <FormLabel>Age at diagnosis?</FormLabel>
        <IntegerDetails
          value={qualifiers[0]?.numericAgeAtDiagnosis ?? ''}
          onChange={(value) => {
            handleQualifiersChange(0, {
              numericAgeAtDiagnosis: value,
            })
          }}
        />
      </FormControl>
    ) : null
  }

  const renderTreatmentLocation = () => {
    if ('label' in category) {
      return (
        <FormControl key="cancerHospital">
          <FormLabel>Treating hospital or location</FormLabel>
          <TextFieldQuestion
            fullWidth
            inputProps={{ maxLength: 150 }}
            handleChange={(value) => {
              setTreatmentLocation(value)
            }}
            value={treatmentLocation}
          />
        </FormControl>
      )
    }
  }

  const renderPatientAddress = () => {
    if ('label' in category) {
      return (
        <FormControl key="cancerAddress">
          <FormLabel>Address when treated? (enter town/city if unknown)</FormLabel>
          <TextFieldQuestion
            fullWidth
            inputProps={{ maxLength: 150 }}
            handleChange={(value) => {
              setPatientAddress(value)
            }}
            value={patientAddress}
          />
        </FormControl>
      )
    }
  }

  const renderBreastSideSelect = () => {
    if (category.label === 'Breast') {
      return [
        <FormControl key="cancerSide">
          <FormLabel>Which side of the body?</FormLabel>
          <Select
            value={qualifiers[0]?.laterality || ''}
            onChange={({ target: { value } }) => {
              handleQualifiersChange(0, { laterality: value })
            }}
          >
            {CancerTypes.BREAST_LATERALITY.map((i) => (
              <MenuItem key={i.id} value={i.id}>
                {i.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>,
        renderSecondOccurrence(),
      ]
    }
  }

  const renderSecondOccurrence = () => {
    if (cancerType && cancerType.label === 'Breast cancer') {
      return (
        <ConditionalSection
          key="cancerSecondOccurrence"
          label={
            isProband
              ? 'Have you had breast cancer more than once?'
              : 'Has this person had breast cancer more than once?'
          }
          showWhenCondition="Y"
          conditionState={secondBreastCancer}
          onConditionStateChange={(e) => {
            setSecondBreastCancer(e)
          }}
          orderSchema={['Y', 'N', 'U']}
          cleanUpChildQuestions={() => {
            if (qualifiers.length === 2) {
              if (qualifiers[0]) {
                setQualifiers([qualifiers[0]])
              } else {
                setQualifiers([])
              }
            }
          }}
        >
          <FormControl>
            <FormLabel>Which side of the body?</FormLabel>
            <Select
              value={qualifiers[1]?.laterality || ''}
              onChange={({ target: { value } }) => {
                handleQualifiersChange(1, { laterality: value })
              }}
            >
              {CancerTypes.BREAST_LATERALITY.map((i) => (
                <MenuItem key={i.id} value={i.id}>
                  {i.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Age at second diagnosis?</FormLabel>
            <IntegerDetails
              value={qualifiers[1]?.numericAgeAtDiagnosis ?? ''}
              onChange={(value) => {
                handleQualifiersChange(1, {
                  numericAgeAtDiagnosis: value,
                })
              }}
            />
          </FormControl>
        </ConditionalSection>
      )
    }
  }

  return renderContent({
    renderMap: {
      cancerType: renderCancerTypes(),
      cancerDiagnoseAge: renderAgeAtDiagnosis(),
      cancerHospitalLocation: renderTreatmentLocation(),
      cancerTreatmentAddress: renderPatientAddress(),
      cancerSide: renderBreastSideSelect(),
    },
    config,
  })
}
